import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { OverviewKpiSeries } from "@nexus/contracts";
import { formatTopicTitle } from "../OverviewChart/chartPrimitives.js";
import type { TopicDetailContext } from "../../lib/topicDetailContext.js";
import { buildTopicDetailKpiCells } from "../../lib/topicDetailUserKpis.js";
import { useUserKpis } from "@/features/kpi-settings/hooks/useUserKpis";
import { KpiVisibilityToggle } from "@/features/kpi-settings/components/KpiVisibilityToggle";
import { UserKpiGrid } from "@/features/kpi-settings/components/UserKpiGrid";

interface TopicDetailInfoTabProps {
  context: TopicDetailContext;
  kpiSeries: OverviewKpiSeries[];
}

export function TopicDetailInfoTab({ context, kpiSeries }: TopicDetailInfoTabProps) {
  const { t } = useTranslation();
  const [revealHidden, setRevealHidden] = useState(false);
  const {
    entries,
    hidden,
    isLoading,
    createKpiAsync,
    updateKpi,
    reorderKpis,
    setHidden,
    isCreating,
    isUpdating,
  } = useUserKpis("overview");

  const valuesByName = useMemo(
    () => buildTopicDetailKpiCells(entries, kpiSeries, context.topicTitle),
    [entries, kpiSeries, context.topicTitle],
  );

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <h3 className="text-base font-medium leading-snug text-foreground">
          {formatTopicTitle(context.topicTitle)}
        </h3>
        {context.topicDescription && (
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {context.topicDescription}
          </p>
        )}
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("overview.topicDetail.metrics", { defaultValue: "Metrics" })}
          </p>
          <KpiVisibilityToggle
            revealHidden={revealHidden}
            hiddenCount={hidden.length}
            onToggle={setRevealHidden}
          />
        </div>

        {entries.length === 0 && !isLoading ? (
          <p className="text-sm text-muted-foreground">
            {t("overview.topicDetail.noMetrics", { defaultValue: "No metrics available for this topic." })}
          </p>
        ) : (
          <UserKpiGrid
            scope="overview"
            entries={entries}
            valuesByName={valuesByName}
            revealHidden={revealHidden}
            isLoading={isLoading}
            isSubmitting={isCreating || isUpdating}
            onReorder={reorderKpis}
            onCreate={async (body) => {
              await createKpiAsync(body);
            }}
            onUpdate={(id, body) => updateKpi({ id, body })}
            onToggleHidden={(entry) => setHidden(entry.id, !entry.hidden)}
          />
        )}
      </div>
    </div>
  );
}
